import { businesses, managers, sessions, statistics } from '~api';
import { Session } from '~models';
import shuffle from 'lodash/shuffle';
import { defineTrackviewOptions, Handler, TrackView } from '~services';
import router from '~router';
import { getField, updateField } from 'vuex-map-fields';
import i18n from '../i18n';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

const isSameOrAfter = require('dayjs/plugin/isSameOrAfter');
dayjs.extend(isSameOrAfter);

const SET_SESSION = 'SET_SESSION';
const SET_AUDIENCE = 'SET_AUDIENCE';
const SET_SESSION_IMAGE = 'SET_SESSION_IMAGE';
const SET_SESSION_VIDEO = 'SET_SESSION_VIDEO';
const SET_BANNER_SESSION = 'SET_BANNER_SESSION';
const SET_BANNER_MINUTES_SESSION = 'SET_BANNER_MINUTES_SESSION';
const RESET_SESSION = 'RESET_SESSION';
const SET_INSTRUCTORS = 'SET_INSTRUCTORS';
const SET_BUSINESSES = 'SET_BUSINESSES';
const SET_SESSIONS = 'SET_SESSIONS';
const SET_REPLACE_SESSIONS = 'SET_REPLACE_SESSIONS';
const SET_BUSINESS_LOGO = 'SET_BUSINESS_LOGO';
const SET_IS_BUSINESS_CHALLENGE = 'SET_IS_BUSINESS_CHALLENGE';
const SET_BACK_LINK = 'SET_BACK_LINK';
const SET_SHOW_MOOD = 'SET_SHOW_MOOD';
const SET_EXPIRED_DATE = 'SET_EXPIRED_DATE';
const SET_ARCHIVED_ID = 'SET_ARCHIVED_ID';
const SET_SETTINGS_PARAM = 'SET_SETTINGS_PARAM';
const SET_CONGRATULATION_DIALOG = 'SET_CONGRATULATION_DIALOG';
const LOADING = 'LOADING';

dayjs.extend(utc);
const state = {
    session: { ...new Session() },
    isLoading: false,
    bannerSession: null,
    bannerSessionMinutes: null,
    instructors: [],
    businesses: [],
    sessions: [],
    backLink: null,
    showMood: false,
    replaceWithSessions: [],
    businessLogo: '',
    isChallenge: false,
    isCongratulationDialog: false
};

const actions = {
    /**
     * Show/hide Congratulation Dialog.
     * @param {function} commit
     * @param {object} rootGetters
     */
    setCongratulationDialog({ commit, rootGetters }) {
        if (rootGetters['auth/isChallenge']) {
            commit(SET_CONGRATULATION_DIALOG);
        }
    },

    toggleLoading({ commit }) {
        commit(LOADING);
    },

    /**
     * Set Show Mood (show/hide mood modal).
     * @param {function} commit
     */
    setShowMood({ commit }) {
        commit(SET_SHOW_MOOD);
    },

    /**
     * Set Mood
     * @param {function} dispatch.
     * @param {object} state
     * @param {number} mood
     * @returns {Promise<void>}
     */
    async setMood({ dispatch, state, commit }, mood) {
        let moodData;
        let totalCompletedMeditations = 0;

        const resp = await sessions.vote(state.session.id, { rate: mood });

        // Get User data from local storage
        const user = JSON.parse(window.localStorage.getItem('user'));

        if (resp.ok) {
            // If role is member set track options for Google Analytics
            if (user.role === 'member') {
                const trackOptions = defineTrackviewOptions(
                    user,
                    state.session
                );

                let event;

                if (
                    state.session.todayVote &&
                    state.session.todayVote.rateBefore
                ) {
                    event = 'set-mood-after';
                } else {
                    event = 'set-mood-before';
                }

                // Set Track Options for Google Analytics
                TrackView(event, trackOptions);
            }

            // Get session data
            dispatch('sessions/read', { id: state.session.id }, { root: true });

            if (state.session?.todayVote?.rateBefore) {
                // Check if Challenge TRUE and isset 3 Completed Meditations
                if (user.role && user.role === 'member') {
                    // Get mood data for user
                    moodData = await statistics.moodData();

                    // Count Total Completed Meditations
                    if (moodData.ok && moodData.data?.votes?.length) {
                        moodData.data.votes.map(vote => {
                            totalCompletedMeditations += vote.count || 0;
                        });
                    }

                    // Show Rate Experience Page if Total Completed Meditations is 3
                    if (totalCompletedMeditations === 3) {
                        // Show Success Challenge Page if Total Completed Meditations is 5
                        const trackOptions = {
                            userid: user.id,
                            business:
                                user.business && user.business.name
                                    ? user.business.name
                                    : null
                        };

                        // Set Track Options for Google Analytics
                        TrackView(
                            '5-meditation-challenge-success',
                            trackOptions
                        );

                        // Redirect to page with Success Challenge
                        commit(RESET_SESSION);
                        window.location.href = '/success-experience';
                    } else {
                        // if Total Completed Meditations is not 5 redirect to page in variable 'backLink'
                        commit(RESET_SESSION);
                        window.location.href = '/themes';
                    }
                } else {
                    commit(RESET_SESSION);
                    // If no challenge  and role is not 'member redirect to page in variable 'backLink'
                    window.location.href = '/themes';
                }
            } else {
                // Set Show Mood
                dispatch('sessions/setShowMood', null, { root: true });
            }
        }
        // Show Notification
        Handler.handleResponse(resp);

        return resp;
    },

    /**
     * Skip Mood.
     * @param {function} dispatch
     * @param {object} state
     * @returns {Promise<void>}
     */
    async skipMood({ dispatch, state }) {
        const resp = await sessions.vote(state.session.id, { skip: true });

        if (resp.ok) {
            // Get User data from local storage
            const user = JSON.parse(window.localStorage.getItem('user'));

            if (user.role === 'member') {
                let event;
                const trackOptions = defineTrackviewOptions(
                    user,
                    state.session
                );

                if (
                    state.session.todayVote &&
                    state.session.todayVote.rateBefore
                ) {
                    event = 'skip-mood-after';
                } else {
                    event = 'skip-mood-before';
                }

                // Set Track Options for Google Analytics
                TrackView(event, trackOptions);
            }
            // Get session data
            dispatch('sessions/read', { id: state.session.id }, { root: true });
            // Set Show Mood
            dispatch('sessions/setShowMood', null, { root: true });

            if (state.backLink) {
                if (state.backLink.includes('/sessions')) {
                    // Redirect to session list
                    window.location.href = '/themes';
                } else {
                    // Reset session data
                    dispatch('sessions/resetSession', null, { root: true });

                    // go to 'state.backLink'
                    router.push({ name: 'dashboard.themes' });
                }
            }
        }
        // Show Notification
        Handler.handleResponse(resp);

        return resp;
    },

    /**
     * Reset session state.
     *
     * Also tears down the globally-registered player instance so
     * navigating between sessions (or away from /sessions/:id back to
     * the list then into a new one) doesn't leave the previous audio
     * playing in the background. The session view is wrapped in
     * <keep-alive>, so without this the old <video> element lives on.
     */
    resetSession({ commit, dispatch }) {
        dispatch('player/stop', null, { root: true });
        commit(RESET_SESSION);
    },

    /**
     * Reset session state and redirect to session list.
     * @param {function} commit
     */
    cancel({ commit, state }) {
        if (state.session.theme === 'minutes') {
            router.push({ name: 'dashboard.mindfulMinutes' });
        } else {
            router.push({ name: 'dashboard.sessions' });
        }

        commit(RESET_SESSION);
    },

    /**
     * Upload image for session
     * @param {function} commit
     */
    async uploadImage({ commit }, data) {
        commit(LOADING);

        const successMessage = i18n.t('sessions.edit.successMessageAvatar');
        const resp = await sessions.uploadImage(data);

        if (resp.ok)
            commit(SET_SESSION_IMAGE, { name: data.name, url: resp.data });

        Handler.handleResponse(resp, successMessage, '');

        commit(LOADING);

        return resp;
    },

    /**
     * Upload image for session
     * @param {function} commit
     */
    async uploadVideo({ commit }, data) {
        commit(LOADING);

        const successMessage = i18n.t('managers.edit.successMessageAvatar');
        const resp = await sessions.uploadVideo(data);

        if (resp.ok)
            commit(SET_SESSION_VIDEO, { name: data.name, url: resp.data });

        Handler.handleResponse(resp, successMessage, '');

        commit(LOADING);

        return resp;
    },

    /**
     * Create session account.
     * @param {function} commit
     * @param {object} state
     * @returns {Promise<void>}
     */
    async create({ commit, state }, isMeeting = false) {
        let startedAt;
        const successMessage = i18n.t('sessions.create.successMessage');
        const redirectLink =
            state.session.theme === 'minutes'
                ? 'dashboard.mindfulMinutes'
                : 'dashboard.sessions';

        console.log(state.session, 'state.session');
        let {
            title,
            instructorId,
            tags,
            description,
            isLocked,
            assignedToAll,
            isSuggested,
            date,
            time,
            theme,
            poster,
            video,
            videoName,
            poster_name,
            meetingDetails,
            check_in_duration,
            audience,
            feedLabel
        } = state.session;

        commit(LOADING);

        // Set session StartedAt date
        if (date && time) {
            startedAt = dayjs(`${date} ${time}`);
        }

        const params = {
            title,
            instructorId,
            tags,
            description,
            isLocked,
            theme,
            assignedToAll,
            isSuggested,
            check_in_duration,
            startedAt,
            videoName,
            poster,
            poster_name,
            video,
            feedLabel
        };

        if ((video && videoName !== 'recorded-live') || theme === 'minutes') {
            delete params.startedAt;
        }

        // API Request to Create Session
        let resp;

        if (isMeeting) {
            const meetingData = {
                settings: {}
            };

            params.theme = 'live';

            const { duration, ...rest } = meetingDetails;

            meetingData.topic = title;
            meetingData.start_time = dayjs(startedAt)
                .utc()
                .format('YYYYY-MM-DDTHH:mm:ss');
            meetingData.duration = duration;
            meetingData.settings = rest;
            meetingData.settings.jbh_time = Number(meetingDetails.jbh_time);

            params.meetingDetails = meetingData;
            const respSession = await sessions.createMeeting(params);

            const audienceParams = {
                sessionId: respSession.data.id,
                ...audience
            };

            resp = await sessions.addMeetingAudience(audienceParams);
        } else {
            resp = await sessions.create(params);
        }

        if (resp.ok) {
            commit(RESET_SESSION);
        }

        // Show Notification and make redirect
        Handler.handleResponse(resp, successMessage, null, redirectLink);

        commit(LOADING);
    },

    /**
     * Get Session.
     * @param {function} commit
     * @param  {string} id
     */
    async read({ commit }, { id, isMeeting = false }) {
        // API Request get session by id
        let resp =
            isMeeting || state.session.type === 'meeting'
                ? await sessions.readMeeting(id)
                : await sessions.read(id);

        if (resp.ok) {
            const { data } = resp;

            if (resp.data?.businesses?.length && resp.data.businesses[0]) {
                // Set is business in challenge
                commit(
                    SET_IS_BUSINESS_CHALLENGE,
                    resp.data.businesses[0].isChallenge
                );
            }

            if (isMeeting) {
                const audience = await sessions.readMeetingAudience(data.id);

                if (audience.ok) {
                    const users = [];
                    const businessIds = [];
                    audience.data.forEach(record => {
                        record.user_id && users.push(record.user_id);
                        record.business_id &&
                            businessIds.push(record.business_id);
                    });

                    console.log(
                        { users, businessIds },
                        '{ users, businessIds }'
                    );

                    data.audience = { users, businessIds };
                }
            }

            // Fix for old meditations
            if (data.isLocked === undefined) {
                data.isLocked = false;
            }

            resp.data.archiveSessionId = null;

            // Format expiredAt
            if (resp.data.expiredAt) {
                commit('SET_SETTINGS_PARAM', 'expiresOn');
                data.expiredAt = dayjs(data.expiredAt).format('YYYY-MM-DD');
            }

            // Set instructorId if isset
            if (data && data.instructor && data.instructor.id) {
                data.instructorId = data.instructor.id;
            }

            // Set todayVote object if not exists
            if (!data.todayVote) {
                data.todayVote = {
                    isCompleted: false,
                    before: null,
                    after: null
                };
            }

            // Set businessIds if isset
            if (data && data.businesses && data.businesses.length) {
                // Set empty array of businesses by default
                data.businessIds = [];

                data.businesses.map(el => {
                    data.businessIds.push(el.id);
                });
            }

            // Set date and time
            if (data && data.startedAt) {
                data.date = dayjs(data.startedAt)
                    .add(data.check_in_duration || 0, 'seconds')
                    .format('YYYY-MM-DD');
                data.time = dayjs(data.startedAt)
                    .add(data.check_in_duration || 0, 'seconds')
                    .format('HH:mm');
            }

            // Set poster_name if not exists
            if (!data.poster_name) {
                data.poster_name = null;
            }

            if (data.type === 'meeting') {
                const meetingDetails = {
                    join_before_host: data.awsData?.settings?.join_before_host,
                    duration: data.awsData?.duration,
                    jbh_time: data.awsData?.settings?.jbh_time,
                    waiting_room: data.awsData?.settings?.waiting_room
                };

                data.meetingDetails = meetingDetails;
            }

            commit(SET_SESSION, { ...data });
        } else {
            // Handle response if error
            Handler.handleResponse(resp);
        }
    },

    /**
     * Get Instructors for select.
     * @param {function} commit
     * @param  {string} id
     */
    async readInstructors({ commit }) {
        let instructorsList = [];

        // Set params for API Request
        let params = {
            role: 'instructor',
            flat: true
        };

        // API Request get instructors for select
        const resp = await managers.list(params);

        if (resp.ok) {
            // prepare instructors list
            resp.data.map(el => {
                instructorsList.push({
                    text: el.fullName,
                    value: el.id
                });
            });

            commit(SET_INSTRUCTORS, instructorsList);
        } else {
            // Handle response if error
            Handler.handleResponse(resp);
        }
    },

    /**
     * Get Businesses for select.
     * @param {function} commit
     * @param  {string} id
     */
    async readBusinesses({ commit }) {
        let businessesList = [];

        // Set params for API Request
        let params = {
            flat: true
        };

        // API Request get businesses for multi-select
        const resp = await businesses.list(params);

        if (resp.ok) {
            // Prepare businesses list
            resp.data.map(el => {
                businessesList.push({
                    text: el.name,
                    value: el.id
                });
            });

            if (resp.data && resp.data.length && resp.data[0]) {
                // Set business logo
                if (resp.data[0].owner && resp.data[0].owner.avatar) {
                    commit(SET_BUSINESS_LOGO, resp.data[0].owner.avatar);
                }
            }

            commit(SET_BUSINESSES, businessesList);
        } else {
            // Handle response if error
            Handler.handleResponse(resp);
        }
    },

    /**
     * Update Session.
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async update({ commit, state }) {
        let startedAt;
        const successMessage = i18n.t('sessions.edit.successMessage');
        const redirectLink =
            state.session.theme === 'minutes'
                ? 'dashboard.mindfulMinutes'
                : 'dashboard.sessions';

        let {
            title,
            instructorId,
            tags,
            description,
            isLocked,
            isArchived,
            assignedToAll,
            isSuggested,
            theme,
            date,
            time,
            check_in_duration,
            poster,
            video,
            videoName,
            poster_name,
            startTime,
            endTime,
            type,
            meetingDetails,
            audience,
            feedLabel
        } = state.session;

        commit(LOADING);

        // Set session StartedAt date
        if (date && time) {
            startedAt = dayjs(`${date} ${time}`);
        }

        const params = {
            title,
            instructorId,
            theme,
            tags,
            description,
            isLocked,
            assignedToAll,
            isSuggested,
            isArchived,
            startedAt,
            check_in_duration,
            poster,
            poster_name,
            video,
            videoName,
            startTime: parseInt(startTime, 10),
            endTime: parseInt(endTime, 10),
            feedLabel
        };

        if (video || theme === 'minutes') {
            delete params.startedAt;
        }

        let resp;

        if (type === 'meeting') {
            const meetingData = {
                settings: {}
            };

            const { duration, ...rest } = meetingDetails;

            if (dayjs(state.session.startedAt).diff(startedAt, 'seconds')) {
                meetingData.start_time = dayjs(startedAt)
                    .utc()
                    .format('YYYYY-MM-DDTHH:mm:ss');
            }
            meetingData.topic = title;
            //
            meetingData.duration = duration;
            meetingData.settings = rest;
            meetingData.settings.jbh_time = Number(meetingDetails.jbh_time);

            params.meetingDetails = meetingData;
            resp = await sessions.updateMeeting(state.session.id, params);

            resp = await sessions.updateMeetingAudience(
                state.session.id,
                audience
            );
        } else {
            resp = await sessions.update(state.session.id, params);
        }

        // Show Notification and make redirect
        Handler.handleResponse(resp, successMessage, null, redirectLink);

        if (resp.ok) {
            commit(RESET_SESSION);
        }

        commit(LOADING);
    },

    async stopSession({ dispatch }, sessionId) {
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.stopSession'),
            { root: true }
        );
        if (!confirmed) return;

        const resp = await sessions.stopSession(sessionId);

        return resp.ok;
    },

    /**
     * Force planned Session start.
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    forcePlannedStart(_, sessionId) {
        return sessions.forcePlannedStart(sessionId);
    },

    /**
     * Delete Session.
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     * @returns {Promise<void>}
     */
    async deleteSession({ commit, dispatch, state }) {
        const successMessage = i18n.t('sessions.edit.delete.successMessage');
        const redirectLink =
            state.session.theme === 'minutes'
                ? 'dashboard.mindfulMinutes'
                : 'dashboard.sessions';

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.deleteSession'),
            { root: true }
        );
        if (!confirmed) return;

        commit(LOADING);

        // API Request on delete session
        let resp;
        if (state.session.type === 'meeting') {
            resp = await sessions.deleteZoom(state.session.id);
        } else {
            resp = await sessions.delete(state.session.id);
        }

        // Show Notification and make redirect
        Handler.handleResponse(resp, successMessage, null, redirectLink);

        if (resp.ok) {
            commit(RESET_SESSION);
        }

        commit(LOADING);
    },

    /**
     * Get Replace with sessions.
     * @returns {Promise<*>}
     */
    async getReplaceWithSessions({ commit }) {
        let sessionsList = [
            {
                text: 'None',
                value: null
            }
        ];

        let params = { flat: true, type: 'recorded' };

        // API Request to get session list with type recorded
        const resp = await sessions.list(params);

        if (resp.ok) {
            // Prepare session list
            resp.data.map(item => {
                sessionsList.push({
                    text: item.title,
                    value: item.id
                });
            });

            commit(SET_REPLACE_SESSIONS, sessionsList);
        } else {
            // Handle response
            Handler.handleResponse(resp);
        }
    },

    /**
     * Get Replace with sessions.
     * @returns {Promise<*>}
     */
    async getFlatSessions({ commit }) {
        let list = [];
        let params = { flat: true, type: 'recorded' };

        // API Request to get session list with type recorded
        const resp = await sessions.list(params);

        if (resp.ok) {
            // Prepare session list
            resp.data.map(item => {
                list.push({
                    name: item.title,
                    id: item.id
                });
            });

            commit(SET_SESSIONS, list);
        } else {
            // Handle response
            Handler.handleResponse(resp);
        }
    },
    /**
     * Get Replace with sessions.
     * @returns {Promise<*>}
     */
    // eslint-disable-next-line no-unused-vars
    async getRandomSessions({ commit }, session) {
        const { theme, id } = session;
        let list = [];
        let params = { theme };

        // API Request to get session list with type recorded
        const resp = await sessions.list(params);

        if (resp.ok) {
            list = resp.data?.items.filter(item =>
                item?.id !== id &&
                !item?.isLocked &&
                item?.businessSession?.length
                    ? !item?.businessSession[0]?.isLocked
                    : false
            );

            if (list.length > 4) {
                list = shuffle(list).slice(0, 3);
            }

            return list;
        } else {
            // Handle response
            Handler.handleResponse(resp);
        }
    },

    /**
     * Get Mindful Minutes session.
     * @returns {Promise<*>}
     */
    async getBannerSessionMinutes({ commit }) {
        let paramsMinutes = { theme: 'minutes' };

        // API Request to get session list with type recorded
        const resp = await sessions.list(paramsMinutes);

        if (resp.ok && resp.data.items) {
            const list = resp.data.items.filter(item => !item.deletedAt);
            commit(SET_BANNER_MINUTES_SESSION, list[0]);
        }
    },

    /**
     * Get Replace with sessions.
     * @returns {Promise<*>}
     */
    async getBannerSession({ commit }) {
        let list = [];
        let paramsPlanned = {
            types: ['planned', 'recorded-live', 'live'],
            limit: 20,
            theme: 'live'
        };

        // API Request to get session list with type recorded
        const resp = await sessions.list(paramsPlanned);
        const currentDate = dayjs();
        const dateWithTimeToChek = currentDate.subtract(15, 'm');

        if (resp.data.items.length)
            list = resp.data.items.filter(item =>
                dayjs(item.startedAt).isSameOrAfter(dateWithTimeToChek)
            );

        console.log(list, 'list');
        if (list.length > 1) {
            list.sort((a, b) => {
                const dateA = dayjs(a.startedAt);
                const dateB = dayjs(b.startedAt);
                return dateA.diff(dateB);
            });
        }

        commit(SET_BANNER_SESSION, list[0]);
    },

    /**
     * Get all sessions except archived.
     * @returns {Promise<*>}
     */
    async getFlatSessionsAll({ commit }) {
        let list = [];
        let params = { flat: true };

        // API Request to get session list
        const resp = await sessions.list(params);

        if (resp.ok) {
            // Prepare session list
            resp.data.map(item => {
                const duration = item.duration
                    ? ` - ${parseInt(
                          item.duration / 60,
                          10
                      )}min ${item.duration % 60}sec`
                    : '';
                const instructor = item.instructor
                    ? ` - ${item.instructor.fullName}`
                    : '';

                if (item.type !== 'processing') {
                    list.push({
                        title: `${item.title}${instructor}${duration}`,
                        id: item.id,
                        isLocked: item.isLocked
                    });
                }
            });

            commit(SET_SESSIONS, list);
        } else {
            // Handle response
            Handler.handleResponse(resp);
        }
    },

    /**
     * Get all sessions except archived.
     * @returns {Promise<*>}
     */
    async getAllSessionsWithInstrutor({ commit }) {
        // API Request to get session list
        const resp = await sessions.getAllWithInstructor();

        if (resp.ok) {
            commit(SET_SESSIONS, resp.data);
        } else {
            // Handle response
            Handler.handleResponse(resp);
        }
    },

    /**
     * Update session save settings.
     * @param {function} commit
     * @param {string} val
     */
    updateSaveSettings({ commit, state }) {
        switch (state.settingsParam) {
            case 'evergreen':
                commit('SET_EXPIRED_DATE', null);
                commit('SET_ARCHIVED_ID', null);
                break;

            case 'replaceWith':
                commit('SET_EXPIRED_DATE', null);
                break;

            case 'expiresOn':
                commit('SET_ARCHIVED_ID', null);
                break;
        }
    },

    /**
     * TODO: remove if not need
     * Save session viewed time.
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     */
    async saveViewedTime({ commit }, data) {
        // Api request to save viewed session time
        const resp = await sessions.setWatched(data.id);

        if (resp.ok) {
            commit(RESET_SESSION);
        }
    }
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,
    [SET_SESSION](state, data) {
        state.session = data;

        state.isLoading = false;
    },
    [SET_BANNER_SESSION](state, data) {
        state.bannerSession = data;
    },
    [SET_BANNER_MINUTES_SESSION](state, data) {
        state.bannerSessionMinutes = data;
    },
    [SET_SHOW_MOOD](state) {
        state.showMood = !state.showMood;
    },
    [SET_ARCHIVED_ID](state, data) {
        state.session.archiveSessionId = data;
    },
    [SET_EXPIRED_DATE](state, data) {
        state.session.expiredAt = data;
    },
    [SET_SETTINGS_PARAM](state, data) {
        state.settingsParam = data;
    },
    [RESET_SESSION](state) {
        state.session = { ...new Session() };
        state.showMood = false;
        state.backLink = null;
        state.isLoading = false;
    },
    [SET_SESSIONS](state, data) {
        state.sessions = data;
    },
    [SET_REPLACE_SESSIONS](state, data) {
        state.replaceWithSessions = data;
    },
    [SET_CONGRATULATION_DIALOG](state) {
        state.isCongratulationDialog = !state.isCongratulationDialog;
    },
    [SET_BACK_LINK](state, data) {
        state.backLink = data;
    },
    [SET_SESSION_IMAGE](state, data) {
        state.session.photo = data.name;
        state.session.photoName = data.url;
    },
    [SET_INSTRUCTORS](state, data) {
        state.instructors = data;
    },
    [SET_SESSION_VIDEO](state, data) {
        state.video = data;
    },
    [SET_BUSINESSES](state, data) {
        state.businesses = data;
    },
    [SET_IS_BUSINESS_CHALLENGE](state, data) {
        state.isChallenge = data;
    },
    [SET_BUSINESS_LOGO](state, data) {
        state.businessLogo = data;
    },
    [LOADING](state) {
        state.isLoading = !state.isLoading;
    },
    [SET_AUDIENCE](state, data) {
        console.log(data, 'data');
        state.session = { ...state.session, audience: data };
        console.log(state.session, 'state.session.audience');
    }
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    session: state => state.session,
    moodBefore: state => state.session.todayVote.rateBefore,
    bannerSession: state => state.bannerSession,
    bannerSessionMinutes: state => state.bannerSessionMinutes,
    isVoted: state => {
        const { isCompleted, isSkip } = state.session.todayVote;

        return isCompleted || isSkip;
    },
    showMood: state => state.showMood,
    isLoading: state => state.isLoading,
    backLink: state => state.backLink,
    sessions: state => state.sessions,
    replaceWithSessions: state => state.replaceWithSessions,
    instructors: state => state.instructors,
    businesses: state => state.businesses,
    businessLogo: state => state.businessLogo,
    isChallenge: state => state.isChallenge,
    isCongratulationDialog: state => state.isCongratulationDialog
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
